import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Table } from "../../common/table/Table";
import { REPORT_SCREEN_VIEWS, REPORT_SCREENS } from "./dashboardReports.queries";
import { PickedMember } from "./MemberPicker";
import { formatCount, formatDateTime } from "./reportFormat";
import { rangeVariables, ReportFilterState } from "./ReportFilters";
import { ResultView } from "./reportParams";
import { BarList, MemberLink, ReportError, Section, StatTile, TileRow } from "./reportParts";

/** The rest fold behind "Show all", so the long tail of one-off screens does not bury the top. */
const TOP_SCREENS = 10;

const VIEW_COLUMNS = [
  { label: "When", key: "viewedAt", skipSorting: true, minWidth: "170px", rowRender: (view: any) => formatDateTime(view.viewedAt) },
  { label: "Member", key: "member", skipSorting: true, rowRender: (view: any) => <MemberLink member={view.member} /> },
  { label: "Screen", key: "label", skipSorting: true },
  {
    label: "What the app logged",
    key: "raw",
    skipSorting: true,
    rowRender: (view: any) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
        {view.raw}
      </Typography>
    ),
  },
];

/**
 * Which screens members visit and how often. The app logs every screen twice
 * (a route path and a route name, milliseconds apart); the server counts them
 * as one view, and folds ids so /business/4121 is "Business page".
 */
export const ScreensTab: React.FC<{ filters: ReportFilterState; member: PickedMember | null; view: ResultView }> = ({
  filters,
  member,
  view,
}) => {
  const [screen, setScreen] = useState<{ screen: string; label: string } | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showAll, setShowAll] = useState(false);
  const range = rangeVariables(filters);

  const question = JSON.stringify([range, member?.id, screen?.screen, rowsPerPage]);
  const [paging, setPaging] = useState({ question, page: 0 });
  const page = paging.question === question ? paging.page : 0;
  const setPage = (next: number) => setPaging({ question, page: next });

  const screens = useQuery(REPORT_SCREENS, {
    variables: { range, userId: member?.id ?? null },
    fetchPolicy: "cache-and-network",
  });
  const views = useQuery(REPORT_SCREEN_VIEWS, {
    variables: {
      range,
      filter: { screen: screen?.screen ?? null, userId: member?.id ?? null },
      page: { take: rowsPerPage, skip: page * rowsPerPage },
    },
    fetchPolicy: "cache-and-network",
  });

  const report = screens.data?.reportScreens;
  const rows: any[] = report?.screens ?? [];
  const share = (views: number) => (report?.totalViews ? Math.round((views / report.totalViews) * 100) : 0);
  const shown = showAll ? rows : rows.slice(0, TOP_SCREENS);
  const numeric = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" } as const;

  return (
    <Stack spacing={2}>
      <ReportError error={screens.error ?? views.error} />
      <Section
        title="Screens"
        note="Each visit counted once, however the app logged it. Click a screen to see who visited it and when."
      >
        {report && (
          <Box sx={{ mb: 2 }}>
            <TileRow>
              <StatTile label="Screen views" value={formatCount(report.totalViews)} />
              <StatTile label="Members" value={formatCount(report.totalMembers)} caption="Who viewed at least one screen" />
              <StatTile label="Screens visited" value={formatCount(rows.length)} />
              {rows[0] && (
                <StatTile
                  label="Most visited"
                  value={rows[0].label}
                  valueSize={20}
                  caption={`${share(rows[0].views)}% of all views`}
                />
              )}
            </TileRow>
          </Box>
        )}
        {screens.loading && <LinearProgress />}
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
          {view === "chart" ? (
            <Box sx={{ p: 1 }}>
              {report && rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                  No screen views in this range.
                </Typography>
              )}
              <BarList
                title="Screen views by screen"
                selected={screen?.screen}
                rows={shown.map((row) => ({
                  key: row.screen,
                  label: row.label,
                  value: row.views,
                  detail: `${share(row.views)}% of views, ${formatCount(row.members)} members`,
                }))}
                onPick={(key) => setScreen(screen?.screen === key ? null : rows.find((row) => row.screen === key))}
              />
            </Box>
          ) : (
            /* Scrolls sideways in a narrow window. */
            <Box sx={{ overflowX: "auto" }}>
            <MuiTable size="small" sx={{ minWidth: 640, "& td, & th": { borderColor: "divider" } }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: "action.hover", color: "text.secondary", fontWeight: 600, fontSize: 12 } }}>
                  <TableCell sx={{ width: 40 }} align="right">
                    #
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>Screen</TableCell>
                  <TableCell align="right">Views</TableCell>
                  <TableCell align="right">Share</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell align="right">Per member</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                      No screen views in this range.
                    </TableCell>
                  </TableRow>
                )}
                {shown.map((row, index) => {
                  const selected = screen?.screen === row.screen;
                  return (
                    <TableRow
                      key={row.screen}
                      hover
                      selected={selected}
                      onClick={() => setScreen(selected ? null : row)}
                      sx={{
                        cursor: "pointer",
                        "& td": { py: 1.25 },
                        "& td:first-of-type": {
                          boxShadow: selected ? (theme) => `inset 3px 0 0 ${theme.palette.primary.main}` : "none",
                        },
                      }}
                    >
                      <TableCell align="right" sx={{ color: "text.secondary", ...numeric }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 500 }}>{row.label}</Box>
                        {row.label !== row.screen && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                            {/* A long path wraps after a slash, never inside a word. */}
                            {row.screen.split("/").map((part: string, i: number) => (
                              <React.Fragment key={i}>
                                {i > 0 && (
                                  <>
                                    /<wbr />
                                  </>
                                )}
                                {part}
                              </React.Fragment>
                            ))}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, ...numeric }}>
                        {formatCount(row.views)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary", ...numeric }}>
                        {share(row.views)}%
                      </TableCell>
                      <TableCell align="right" sx={numeric}>
                        {formatCount(row.members)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary", ...numeric }}>
                        {row.members ? (row.views / row.members).toFixed(1) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
            </Box>
          )}
          {rows.length > TOP_SCREENS && (
            <Box sx={{ borderTop: 1, borderColor: "divider", textAlign: "center", py: 0.5 }}>
              <Button size="small" onClick={() => setShowAll(!showAll)}>
                {showAll ? `Show the top ${TOP_SCREENS}` : `Show all ${rows.length} screens`}
              </Button>
            </Box>
          )}
        </Box>
      </Section>

      <Section title={screen ? `Visits to ${screen.label}` : "Every screen view"} note="Newest first.">
        {screen && <Chip label={`${screen.label} ×`} onClick={() => setScreen(null)} sx={{ mb: 1 }} />}
        {views.loading && <LinearProgress />}
        <Table
          loading={views.loading}
          rows={views.data?.reportScreenViews.views ?? []}
          columns={VIEW_COLUMNS}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          total={views.data?.reportScreenViews.total ?? 0}
        />
      </Section>
    </Stack>
  );
};
